import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { ProposalService } from './proposal.service';
import { lastValueFrom, map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DocumentService {
  constructor(private http: HttpClient) {}

  // Fetches the enabled license requirements applicable to the authenticated
  // professional associate (resolved server-side against their offered service
  // types/sub-types) from the PA-facing GET /license-requirements endpoint. The
  // response is the PA ApiResponse envelope; the caller works with the unwrapped
  // `.data` array of requirement definitions ({ id, documentTitle,
  // zoneRequirement, states, issueDateRequired, expirationDateRequired,
  // associations }). Returns [] when the envelope carries no data.
  fetchApplicableLicenseRequirements(): Observable<any[]> {
    return this.http
      .get<any>(environment.apiUrl + '/license-requirements', {
        responseType: 'json',
      })
      .pipe(map((response: any) => response?.data ?? []));
  }

  // `note` is optional — the ALTA "Upload Survey" note field. When present
  // (non-empty string) it's sent as the `note` form part, matching the
  // connect-service `@RequestPart(value = "note", required = false)` on
  // /document/proposal. Omitted (or blank) leaves the upload unchanged.
  uploadPropsalDocuments(documents: any, proposal: any, note?: string) {
    const formData = new FormData();
    formData.set(
      'proposal',
      new Blob([JSON.stringify(proposal)], {
        type: 'application/json',
      })
    );
    documents.forEach((docRequest: any) =>
      formData.append('files', docRequest.file)
    );
    if (note?.trim()) {
      formData.set('note', note.trim());
    }
    return this.http.post<any>(
      environment.apiUrl + '/document/proposal',
      formData,
      {
        responseType: 'json',
      }
    );
  }

  // The PA's post-visit report photos, on POST /document/site-visit. `type` is exactly ONE of the two
  // report photo kinds per call — the handler refuses anything else and there is no third value — so
  // an access-issue photo and a concern photo are always two separate calls, never one with a mixed
  // file list. `siteVisit` mirrors `SiteVisitDocumentUploadRequest` field for field: `visitId` and
  // `type` only. No vendor id anywhere in the request — the acting PA is resolved server-side from the
  // authenticated principal, and the negotiation's ownership is checked against that, not against
  // anything this side sends. The response's `data` is the created document ids, `List<UUID>` on the
  // wire — the completion transition then carries them back as `accessIssuePhotoIds`/`concernPhotoIds`.
  uploadSiteVisitDocuments(
    visitId: string,
    type: 'SITE_VISIT_ACCESS_ISSUE' | 'SITE_VISIT_CONCERN',
    files: File[]
  ) {
    const formData = new FormData();
    formData.set(
      'siteVisit',
      new Blob([JSON.stringify({ visitId, type })], {
        type: 'application/json',
      })
    );
    files.forEach((file) => formData.append('files', file));
    return this.http.post<any>(
      environment.apiUrl + '/document/site-visit',
      formData,
      {
        responseType: 'json',
      }
    );
  }

  uploadProfileDocuments(user: any, files: FileList) {
    const formData = new FormData();
    formData.set(
      'user',
      new Blob([JSON.stringify(user)], {
        type: 'application/json',
      })
    );
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    return this.http.post<any>(
      environment.apiUrl + '/document/profile',
      formData,
      {
        responseType: 'json',
      }
    );
  }

  // `coverage` is optional. When provided it is sent as the `coverage` JSON multipart
  // part (serviceType + serviceSubTypes type keys + expirationDate, plus the
  // connect-only licenseRequirementId + issueDate when the upload satisfies a named
  // license requirement) that the connect-service /document/zone endpoint reads via
  // @RequestPart(value="coverage", required=false). Existing callers that omit it
  // keep the pre-coverage behaviour.
  uploadServiceZoneDocuments(
    user: any,
    serviceZone: any,
    files: FileList,
    coverage?: any
  ) {
    const formData = new FormData();
    formData.set(
      'user',
      new Blob([JSON.stringify(user)], {
        type: 'application/json',
      })
    );
    formData.set(
      'serviceZone',
      new Blob([JSON.stringify(serviceZone)], {
        type: 'application/json',
      })
    );
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }
    if (coverage) {
      formData.set(
        'coverage',
        new Blob([JSON.stringify(coverage)], {
          type: 'application/json',
        })
      );
    }

    return this.http.post<any>(
      environment.apiUrl + '/document/zone',
      formData,
      {
        responseType: 'json',
      }
    );
  }

  // Updates an existing zone document's coverage (service association) and/or
  // expiration via the vendor PATCH /document/zone/{documentId} endpoint. `body`
  // carries { userId, serviceZoneIdentifier, serviceType, serviceSubTypes,
  // expirationDate, licenseRequirementId, issueDate }. The coverage edit is a FULL
  // replacement, so the caller must always send the document's current
  // licenseRequirementId to preserve the link (omitting it clears the link).
  // Server-side, changing the coverage (serviceType, serviceSubTypes, OR
  // licenseRequirementId) resets that document's reviewStatus to PENDING; an
  // expiration- or issue-date-only change leaves it untouched. Returns the updated
  // user.
  updateZoneDocumentCoverage(userId: string, documentId: string, body: any) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http.patch<any>(
      environment.apiUrl + `/document/zone/${documentId}`,
      body,
      {
        headers,
        responseType: 'json',
      }
    );
  }

  deleteProposalDocument(documentId: string) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    const params = { documentId: documentId };
    return this.http.delete<any>(environment.apiUrl + '/document/proposal', {
      headers,
      responseType: 'json',
      params: params,
    });
  }

  deleteProfileDocument(userId: string, documentId: string) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    const params = { userId: userId, documentId: documentId };
    return this.http.delete<any>(environment.apiUrl + '/document/profile', {
      headers,
      responseType: 'json',
      params: params,
    });
  }

  deleteZoneDocument(
    userId: string,
    documentId: string,
    serviceZoneIdentifier: string
  ) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    const params = {
      userId: userId,
      documentId: documentId,
      serviceZoneIdentifier: serviceZoneIdentifier,
    };
    return this.http.delete<any>(environment.apiUrl + '/document/zone', {
      headers,
      responseType: 'json',
      params: params,
    });
  }

  downloadDocument(downloadDocument: any) {
    if ((downloadDocument.type === 'ATP' || downloadDocument.type === 'SURVEY_CHECKLIST') && downloadDocument.documentPath) {
      window.open(downloadDocument.documentPath, '_blank');
      return;
    } else {
      return this.http
        .get<any>(
          environment.apiUrl +
            `/document/download?documentId=${downloadDocument.id}`,
          {
            responseType: 'blob' as 'json',
          }
        )
        .subscribe((blob) => {
          const data = window.URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.download = downloadDocument.fileName;
          link.href = data;
          link.click();

          setTimeout(() => {
            window.URL.revokeObjectURL(data);
          }, 100);
        });
    }
  }

  async downloadPdfToViewer(downloadDocument: any): Promise<{ url: string, filename: string }> {
    if ((downloadDocument.type === 'ATP' || downloadDocument.type === 'SURVEY_CHECKLIST') && downloadDocument.documentPath) {
      return { url: downloadDocument.documentPath, filename: downloadDocument.fileName };
    } else {
      try {
        const blob = await lastValueFrom(
          this.http.get(environment.apiUrl + `/document/download?documentId=${downloadDocument.id}`, {
            responseType: 'blob' as 'json',
          })
        ) as Blob;
        const data = window.URL.createObjectURL(blob);
         return { url: data, filename: downloadDocument.fileName };
      } catch (error) {
        throw error;
      }
    }
  }
}
